import {
  CollectionConfigSchema,
  basename,
  getField,
  runQuery,
  type CollectionConfig,
  type CollectionItemConfig,
  type IndexEntry,
} from '@hud/core';
import { registerWidget, type WidgetProps } from '../registry.js';
import { useHud } from '../context.js';
import { useStore } from '../hooks/useStore.js';
import './collection.css';

/**
 * Query the vault, render the results.
 *
 * This is the widget that keeps the HUD extensible without code. "Track the
 * books I'm reading" is a folder of markdown files and one config — the same
 * machinery that renders goals, films, workouts, or anything else with
 * frontmatter.
 */
function Collection({ config }: WidgetProps<CollectionConfig>) {
  const store = useStore();

  const results = runQuery(store.index(), {
    from: config.from,
    ...(config.where ? { where: config.where } : {}),
    ...(config.sort ? { sort: config.sort } : {}),
    ...(config.limit !== undefined ? { limit: config.limit } : {}),
  });

  if (results.length === 0) {
    return <div class="hud-collection__empty">{config.empty}</div>;
  }

  return (
    <ul class={`hud-collection hud-collection--${config.item.layout}`}>
      {results.map((entry) => (
        <li class="hud-collection__item" key={entry.path}>
          <Item entry={entry} item={config.item} />
        </li>
      ))}
    </ul>
  );
}

/** Open a result's source file in the editor. Every row is a real markdown note. */
function EditButton({ path }: { path: string }) {
  const { openEditor } = useHud();
  return (
    <button
      class="hud-collection__edit"
      type="button"
      aria-label={`Edit ${path}`}
      onClick={() => openEditor(path)}
    >
      ✎
    </button>
  );
}

function Item({ entry, item }: { entry: IndexEntry; item: CollectionItemConfig }) {
  // Falling back to the filename means a file with no frontmatter still shows
  // something recognisable instead of a blank row.
  const title = text(getField(entry.data, item.title)) || basename(entry.path);

  if (item.layout === 'progress') {
    return <ProgressItem entry={entry} item={item} title={title} />;
  }

  const subtitle = item.subtitle ? text(getField(entry.data, item.subtitle)) : '';
  const meta = item.meta ? text(getField(entry.data, item.meta)) : '';
  const badge = item.badge ? text(getField(entry.data, item.badge)) : '';

  return (
    <>
      <div class="hud-collection__main">
        <div class="hud-collection__title">{title}</div>
        {subtitle ? <div class="hud-collection__subtitle">{subtitle}</div> : null}
      </div>
      {badge ? <span class="hud-collection__badge">{badge}</span> : null}
      {meta ? <span class="hud-collection__meta">{meta}</span> : null}
      <EditButton path={entry.path} />
    </>
  );
}

function ProgressItem({
  entry,
  item,
  title,
}: {
  entry: IndexEntry;
  item: CollectionItemConfig;
  title: string;
}) {
  const current = number(getField(entry.data, item.value));
  const target = number(getField(entry.data, item.target));
  const unit = item.unit ? text(getField(entry.data, item.unit)) : '';

  const store = useStore();
  const hasProgress = current !== null && target !== null && target > 0;
  const ratio = hasProgress ? Math.min(current / target, 1) : 0;
  const percent = Math.round(ratio * 100);

  /** Nudge the goal's `current` and write it back to that file's frontmatter. */
  const bump = (delta: number) => {
    const next = Math.max(0, (current ?? 0) + delta);
    store.mutate({
      kind: 'patchFrontmatter',
      path: entry.path,
      patch: { [item.value]: next },
    });
  };

  return (
    <>
      <div class="hud-collection__progress-head">
        <span class="hud-collection__title">{title}</span>
        <span class="hud-collection__meta">
          {hasProgress ? `${format(current)} / ${format(target)}${unit ? ` ${unit}` : ''}` : '—'}
        </span>
        <span class="hud-collection__bump">
          <button type="button" aria-label={`Decrease ${title}`} onClick={() => bump(-1)}>
            −
          </button>
          <button type="button" aria-label={`Increase ${title}`} onClick={() => bump(1)}>
            +
          </button>
        </span>
      </div>
      <div
        class="hud-collection__bar"
        role="progressbar"
        aria-valuenow={percent}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={title}
      >
        <div class="hud-collection__bar-fill" style={{ width: `${percent}%` }} />
      </div>
    </>
  );
}

/** Render any frontmatter value as a single line. */
function text(value: unknown): string {
  if (value === undefined || value === null) return '';
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  if (Array.isArray(value)) return value.map(text).filter(Boolean).join(', ');
  if (typeof value === 'object') return '';
  return String(value);
}

function number(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function format(value: number): string {
  return value.toLocaleString('en-US');
}

registerWidget({
  type: 'collection',
  schema: CollectionConfigSchema,
  component: Collection,
});
